import { cn } from "@tellescope/ui";
import type { SectionBlock as SectionBlockType, LeafBlock } from "../../../shared/schema";
import { useBlockVisibility } from "../../hooks/useBlockVisibility";
import { TextBlock } from "./TextBlock";
import { HeadingBlock } from "./HeadingBlock";
import { ButtonBlock } from "./ButtonBlock";
import { CardBlock } from "./CardBlock";
import { SpacerBlock } from "./SpacerBlock";
import { DividerBlock } from "./DividerBlock";
import { BannerBlock } from "./BannerBlock";
import { HtmlBlock } from "./HtmlBlock";
import { RichTextBlock } from "./RichTextBlock";
import { PopupBlock } from "./PopupBlock";
import { AppointmentListBlock } from "./AppointmentListBlock/AppointmentListBlock";
import { OrderListBlock } from "./OrderListBlock";
import { ChatRoomListBlock } from "./ChatRoomListBlock";
import { FileListBlock } from "./FileListBlock";
import { FormResponseListBlock } from "./FormResponseListBlock";
import { PinnedFormsListBlock } from "./PinnedFormsListBlock";
import { ProfileEditBlock } from "./ProfileEditBlock";
import { ContentListBlock } from "./ContentListBlock";
import { PurchaseListBlock } from "./PurchaseListBlock";
import { FileUploadBlock } from "./FileUploadBlock";
import { StripeSubscriptionsBlock } from "./StripeSubscriptionsBlock";
import { StripeBillingPortalButtonBlock } from "./StripeBillingPortalButtonBlock/StripeBillingPortalButtonBlock";
import { StripeInvoicesBlock } from "./StripeInvoicesBlock/StripeInvoicesBlock";
import { AppointmentBookingBlock } from "./AppointmentBookingBlock";
import { VitalDataBlock } from "./VitalDataBlock/VitalDataBlock";

interface SectionBlockProps {
  block: SectionBlockType;
}

// Gap size mapping
const gapStyles: Record<SectionBlockType["props"]["gap"], string> = {
  sm: "gap-2", // 8px
  md: "gap-4", // 16px
  lg: "gap-6", // 24px
};

/**
 * Renders a child block within a section.
 * Child blocks in sections don't have their own layout (width) - they inherit from the section.
 */
const SectionChildBlock: React.FC<{ block: LeafBlock }> = ({ block }) => {
  switch (block.type) {
    case "text":
      return <TextBlock content={block.props.content} variant={block.props.variant} />;

    case "heading":
      return <HeadingBlock text={block.props.text} level={block.props.level} />;

    case "button":
      return <ButtonBlock text={block.props.text} variant={block.props.variant} href={block.props.href} />;

    case "card":
      return <CardBlock title={block.props.title} description={block.props.description} />;

    case "spacer":
      return <SpacerBlock size={block.props.size} />;

    case "divider":
      return <DividerBlock />;

    case "banner":
      return (
        <BannerBlock
          content={block.props.content}
          variant={block.props.variant}
          dismissible={block.props.dismissible}
        />
      );

    case "html":
      return <HtmlBlock content={block.props.content} />;

    case "rich-text":
      return <RichTextBlock content={block.props.content} />;

    case "appointment-list":
      return <AppointmentListBlock block={block} />;

    case "order-list":
      return <OrderListBlock block={block} />;

    case "chatroom-list":
      return <ChatRoomListBlock block={block} />;

    case "file-list":
      return <FileListBlock block={block} />;

    case "form-response-list":
      return <FormResponseListBlock block={block} />;

    case "pinned-forms-list":
      return <PinnedFormsListBlock block={block} />;

    case "profile-edit":
      return <ProfileEditBlock block={block} />;

    case "content-list":
      return <ContentListBlock block={block} />;

    case "purchase-list":
      return <PurchaseListBlock block={block} />;

    case "file-upload":
      return <FileUploadBlock block={block} />;

    case "stripe-subscriptions":
      return <StripeSubscriptionsBlock block={block} />;

    case "stripe-billing-portal-button":
      return <StripeBillingPortalButtonBlock block={block} />;

    case "stripe-invoices":
      return <StripeInvoicesBlock block={block} />;

    case "appointment-booking":
      return <AppointmentBookingBlock block={block} />;

    case "vital-data":
      return <VitalDataBlock block={block} />;

    case "popup":
      return <PopupBlock block={block} />;

    default: {
      const _exhaustive: never = block;
      return null;
    }
  }
};

/**
 * Wraps a child block with visibility evaluation using the full async context.
 * Returns null if the block's visibility condition evaluates to false.
 */
const VisibleSectionChildBlock: React.FC<{ block: LeafBlock }> = ({ block }) => {
  const { isVisible } = useBlockVisibility(block);

  if (!isVisible) {
    return null;
  }

  return <SectionChildBlock block={block} />;
};

/**
 * Section block - a container that groups blocks together.
 * Child blocks stack vertically and share the section's responsive width.
 *
 * Each child block independently evaluates its own visibility conditions,
 * supporting all data sources (orders, appointments, form responses, etc.).
 */
export const SectionBlock: React.FC<SectionBlockProps> = ({ block }) => {
  const gap = block.props.gap ?? "md";

  return (
    <div className={cn("flex flex-col", gapStyles[gap])}>
      {block.blocks.map((childBlock) => (
        <VisibleSectionChildBlock key={childBlock.id} block={childBlock} />
      ))}
    </div>
  );
};
